import { useMemo } from 'react';

import { SpinnerIconContainer } from '@eneco/custom-components/flows/Layout';
import { Layout } from '@eneco/flows/components';
import { createFlowProvider } from '@eneco/flows/utils';
import { useRouter } from '@eneco/next';
import { Box } from '@eneco/sparky';
import { SpinnerIcon } from '@eneco/sparky/icons';
import { FeaturesReady } from '@growthbook/growthbook-react';

import { FlowConsumerContextEnergy } from './context';
import { testConfigs } from './eneco/experiments';
import { CallSupportExperiment } from './eneco/experiments/exp-n-607/CallSupportExperiment';
import { ExperimentProvider, useExperimentFlowProvider } from './eneco/growthbook';
import { actions, initialContext, routes as routes_A } from './EnergyFlow.config';
import { getContractQueryParam } from './shared/helpers';

const EXCLUDE_TOU_EXPERIMENT = 'excludeTouExperiment';
const FORCE_CALL_SUPPORT = 'forceCallSupport';

const defaultConfig = {
  routes: routes_A,
  actions: actions,
  initialContext: initialContext,
};

const queryParamNames = [EXCLUDE_TOU_EXPERIMENT, FORCE_CALL_SUPPORT];

const Flow = () => {
  const { query: queryParams } = useRouter();
  const excludeExperiment = Boolean(queryParams[EXCLUDE_TOU_EXPERIMENT]);

  const attributes = useMemo(
    () => ({
      excludeExperiment,
      disableExperiments: queryParams.disableExperiments === 'true',
      forceTestIndex: Number(queryParams.forceTestIndex),
    }),
    [excludeExperiment, queryParams.disableExperiments, queryParams.forceTestIndex],
  );

  return (
    <ExperimentProvider testConfigs={testConfigs} defaultConfig={defaultConfig} attributes={attributes}>
      <FeaturesReady fallback={null} timeout={1000}>
        <EnergyFlow />
      </FeaturesReady>
    </ExperimentProvider>
  );
};

const EnergyFlow = () => {
  const flowExperimentProviderProps = useExperimentFlowProvider();
  const { routes, actions, initialContext, isLoading } = flowExperimentProviderProps;

  const { Provider, Navigation, Router } = useMemo(
    () =>
      createFlowProvider<FlowConsumerContextEnergy>({
        routes,
        actions,
        initialContext,
        stickyQueryParams: queryParamNames,
        completionQueryParams: context => ({ contract: getContractQueryParam(context.orderedOffer) }),
      }),
    [routes, actions, initialContext],
  );

  return (
    <div>
      {isLoading ? (
        <SpinnerIconContainer alignY="center" alignX="center">
          <Box paddingBottom="32">
            <SpinnerIcon size="large" color="iconBrand" />
          </Box>
        </SpinnerIconContainer>
      ) : (
        <Provider>
          <Layout>
            <Layout.Header>
              <Navigation componentName="Navigation" />
            </Layout.Header>
            <Router />
            <CallSupportExperiment />
          </Layout>
        </Provider>
      )}
    </div>
  );
};

export default Flow;
